import { openobserveLogs } from "@openobserve/browser-logs";
import { openobserveRum } from "@openobserve/browser-rum";
import { redactSecrets } from "./redact-secrets";

// `OPENOBSERVE_DOMAIN` is a bare host (e.g. `openobserve.served.to`), or
// `localhost:5080` for the local-dev-setup instance, which only serves HTTP.
const parseSite = (domain: string) => {
	const host = domain.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
	const insecureHTTP =
		/^http:\/\//i.test(domain) ||
		/^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(host);

	return { site: host, insecureHTTP };
};

export const isOpenObserveIntakeUrl = (url: string, host: string): boolean => {
	try {
		const parsed = new URL(url);
		return parsed.host === host && parsed.pathname.startsWith("/rum/");
	} catch {
		return false;
	}
};

let isRumStarted = false;

/**
 * Starts OpenObserve RUM (views, actions, errors, resources, long tasks and
 * session replay) and forwards the browser console as logs. The logs
 * SDK tags each log with the active RUM session id, so a replay's console
 * output is found by its session id. A no-op when the environment has no RUM
 * client token, so local and CI builds send nothing.
 */
export const startRum = () => {
	const domain = process.env.OPENOBSERVE_DOMAIN;
	const clientToken = process.env.OPENOBSERVE_RUM_CLIENT_TOKEN;
	if (isRumStarted || !domain || !clientToken) return;

	const intake = {
		clientToken,
		...parseSite(domain),
		organizationIdentifier: "default",
		apiVersion: "v1",
		service: "staff",
		env: process.env.ENV,
		version: process.env.VERSION,
	};

	openobserveRum.init({
		...intake,
		applicationId: "staff",
		sessionSampleRate: 100,
		sessionReplaySampleRate: 100,
		defaultPrivacyLevel: "mask-user-input",
		trackUserInteractions: true,
		trackResources: true,
		trackLongTasks: true,
		beforeSend: (event) => {
			// The SDK records its own uploads as resources, which can feed a
			// self-amplifying upload loop.
			if (
				event.type === "resource" &&
				isOpenObserveIntakeUrl(event.resource.url, intake.site)
			)
				return false;
			// Errors can embed a failed request's variables, e.g. a login's PIN.
			if (event.type === "error") {
				event.error.message = redactSecrets(event.error.message);
				event.error.stack = redactSecrets(event.error.stack);
			}
			return true;
		},
	});
	openobserveLogs.init({
		...intake,
		sessionSampleRate: 100,
		// The full console, errors included, so one query reads like devtools.
		// Failed requests and uncaught errors are not re-logged: RUM already
		// records them (every request is a resource in `_rumdata`).
		forwardConsoleLogs: "all",
		forwardErrorsToLogs: false,
		beforeSend: (log) => {
			log.message = redactSecrets(log.message);
			if (log.error) {
				log.error.message = redactSecrets(log.error.message);
				log.error.stack = redactSecrets(log.error.stack);
			}
			return true;
		},
	});
	isRumStarted = true;
};

export const setRumVenue = (venueId: string | null | undefined) => {
	if (!isRumStarted) return;
	for (const sdk of [openobserveRum, openobserveLogs]) {
		if (venueId) sdk.setGlobalContextProperty("venueId", venueId);
		else sdk.removeGlobalContextProperty("venueId");
	}
};

type RumStaff = { _id: string; username?: string } | undefined;

// OpenObserve's Sessions list only shows the user's email, so it carries
// `<staffId>@<venueId>`, or the venue alone before a staff logs in, to make each
// session identifiable at a glance.
export const getRumUser = (
	staff: RumStaff,
	venueId: string | null | undefined,
) => {
	if (staff)
		return {
			id: staff._id,
			name: staff.username,
			email: venueId ? `${staff._id}@${venueId}` : undefined,
		};
	return venueId ? { email: venueId } : undefined;
};

export const setRumStaff = (
	staff: RumStaff,
	venueId: string | null | undefined,
) => {
	if (!isRumStarted) return;
	const user = getRumUser(staff, venueId);
	for (const sdk of [openobserveRum, openobserveLogs]) {
		if (user) sdk.setUser(user);
		else sdk.clearUser();
	}
};
